import React, { isValidElement } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
export interface StatCardProps {
  label: string;
  value: string | number;
  badge?: {
    text: string;
    variant: 'paid' | 'pending' | 'processing' | 'warning' | 'error';
  };
  secondaryValue?: string;
  dateRange?: React.ReactNode;
  trend?: React.ReactNode | {
    value: string;
    isPositive: boolean;
  };
  icon?: React.ReactNode;
  onClick?: () => void;
  'data-id'?: string;
}
// Helper to check if trend is an object with {value, isPositive}
function isTrendObject(trend: any): trend is {
  value: string;
  isPositive: boolean;
} {
  return trend !== null && typeof trend === 'object' && !isValidElement(trend) && 'value' in trend && 'isPositive' in trend;
}
export function StatCard({
  label,
  value,
  badge,
  secondaryValue,
  dateRange,
  trend,
  icon,
  onClick,
  'data-id': dataId
}: StatCardProps) {
  const badgeStyles = {
    paid: 'bg-[#D1FAE5] text-[#059669]',
    pending: 'bg-[#FEF3C7] text-[#D97706]',
    processing: 'bg-[#DBEAFE] text-[#2563EB]',
    warning: 'bg-[#FEF3C7] text-[#D97706]',
    error: 'bg-[#FEE2E2] text-[#DC2626]'
  };
  const CardWrapper = onClick ? 'button' : 'div';
  const clickableProps = onClick ? {
    onClick,
    className: `w-full text-left bg-white rounded-lg p-5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20`
  } : {
    className: `bg-white rounded-lg p-5 shadow-sm transition-shadow duration-200`
  };
  // Render trend content - handle both object and ReactNode formats
  const renderTrend = () => {
    if (!trend) return null;
    if (isTrendObject(trend)) {
      const color = trend.isPositive ? '#10B981' : '#EF4444';
      const Icon = trend.isPositive ? TrendingUp : TrendingDown;
      return <div className="flex items-center gap-1 text-xs" style={{
        color
      }} data-id="element-2711">
          <Icon className="w-3 h-3" data-id="element-2712" />
          <span className="font-medium" data-id="element-2713">{trend.value}</span>
        </div>;
    }
    return trend;
  };
  return <CardWrapper {...clickableProps} data-id="element-2714">
      <div className="flex flex-col h-full" data-id="element-2715">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 mb-2 flex-shrink-0" data-id="element-2716">
          <dt className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-2717">
            {label}
          </dt>
          {dateRange && <div onClick={e => e.stopPropagation()} data-id="element-2718">{dateRange}</div>}
        </div>

        <dd className="flex-grow space-y-2" data-id="element-2719">
          <div className="flex items-baseline gap-3" data-id="element-2720">
            <span className="text-[28px] font-semibold text-[#0F172A] leading-none" data-id="element-2721">
              {value}
            </span>
            {badge && <span className={`px-2.5 py-1 rounded-xl text-[12px] font-medium ${badgeStyles[badge.variant]}`} data-id="element-2722">
                {badge.text}
              </span>}
          </div>

          {secondaryValue && <div className="text-[13px] text-[#94A3B8]" data-id="element-2723">{secondaryValue}</div>}

          {trend && <div data-id="element-2724">{renderTrend()}</div>}
        </dd>
      </div>
    </CardWrapper>;
}